export type Mode = "development" | "production";

/**
 * Which environment we are actually running in.
 *
 * Deliberately not derived from NODE_ENV: any built output reports
 * NODE_ENV="production", including a build made to run locally, so it says how
 * the code was compiled rather than where it is deployed. Set
 * NEXT_PUBLIC_MODE=development locally; anything else, including unset, is
 * production — a forgotten flag should fail towards the stricter environment,
 * never quietly relax it.
 *
 * NEXT_PUBLIC_ so that client components can branch on it too. Next only
 * substitutes that prefix into browser bundles, and only where it is written out
 * literally, so keep the `process.env.NEXT_PUBLIC_MODE` member access below
 * exactly as it is — destructuring it stops the substitution and the browser
 * silently falls back to production.
 */
export const mode: Mode = process.env.NEXT_PUBLIC_MODE === "development" ? "development" : "production";
export const isDevelopment = mode === "development";
export const isProduction = !isDevelopment;

/**
 * The site's public origin, used for canonical URLs, social cards and the
 * sitemap. Unset, production falls back to the real domain rather than to a
 * preview deployment or localhost, so a forgotten variable can never point
 * search engines somewhere else. Written out literally for the same reason as
 * NEXT_PUBLIC_MODE above.
 */
export const siteUrl = (
  process.env.NEXT_PUBLIC_BASE_URL || (isDevelopment ? "http://localhost:3333" : "https://sambatista.com")
).replace(/\/+$/, "");

// Server-side only: this module is bundled into the browser now that client
// components read `mode` from it, and a throw at import time would take the
// whole page down. Secrets are never substituted into client bundles, so on the
// browser this check would be asserting against a value it cannot see anyway.
if (typeof window === "undefined" && isProduction && !process.env.INNER_CIRCLE_ACCESS_LIST) {
  throw new Error(`Missing environment variable 'INNER_CIRCLE_ACCESS_LIST'`);
}

export const innerCircleAccessList = (process.env.INNER_CIRCLE_ACCESS_LIST || "")
  .split(",")
  .map((val) => val.toLowerCase());

/**
 * Signs the private links that open a blog draft on the site (src/lib/draft-preview.ts); a new value revokes every
 * link given out. Unset, drafts aren't built for production and 404 there, as before. Development shows each
 * draft's link on its page, so `.env` holds the same value as Vercel, or the links it shows won't open.
 */
export const draftPreviewSecret = process.env.DRAFT_PREVIEW_SECRET || "";

/**
 * Where the browser reads the site's public data (view counts, the subscriber count):
 * https://api.sambatista.com, this same app on the EU Dokku host, so those reads never run as
 * Vercel functions. Unset means the site's own origin, which works anywhere and is what the API
 * host itself would use. Written out literally for the same reason as NEXT_PUBLIC_MODE above.
 */
export const apiUrl = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/+$/, "");

/**
 * The API's own host name, empty when it shares the site's origin. The proxy serves only /api there, and its matcher
 * (src/proxy.ts) repeats the name literally, so a new API host changes both.
 */
export const apiHost = /^https?:\/\/([^/]+)/.exec(apiUrl)?.[1] ?? "";

/**
 * The pages allowed to read the API from another origin: the site, and its Vercel previews.
 * Anything else gets no CORS header, so a browser keeps the response from it.
 */
export const apiAllowedOrigins = [siteUrl, "https://sambatista.com", "https://www.sambatista.com"];
export const apiAllowedOriginPattern = /^https:\/\/sambatista-[a-z0-9-]+-samuel-batistas-projects\.vercel\.app$/;

/**
 * The Discord webhook the contact form posts to (Server Settings, Integrations, Webhooks). Unset, production
 * answers 503 rather than pretending a message was delivered; development logs the message instead, like
 * DataConnector-Pro's local sink, so the form can be tried without posting to the real channel.
 */
export const contactDiscordWebhookUrl = process.env.CONTACT_DISCORD_WEBHOOK_URL || "";

/** sambatista.com's site in Umami: the tracker's `data-website-id`, and the filter on every view count. */
export const umamiWebsiteId = "35a50443-4fd8-4f60-b24e-4eaeec70e6ce";

/**
 * `ANALYTICS_DATABASE_URL` is what `dokku postgres:link --alias ANALYTICS_DATABASE` sets on the
 * EU host. Parsed by hand rather than with `new URL` at import, which throws on a bad value, and
 * this module ships to the browser too.
 */
const analyticsDbUrl =
  /^postgres(?:ql)?:\/\/([^:@/]+):([^@/]*)@([^:/]+):(\d+)\/([^?]+)/.exec(process.env.ANALYTICS_DATABASE_URL || "") ||
  undefined;

/** Connection details for the Umami analytics database, read for page view counts. */
export const analyticsDb = {
  host: analyticsDbUrl?.[3] || process.env.ANALYTICS_DB_HOST,
  port: parseInt(analyticsDbUrl?.[4] || process.env.ANALYTICS_DB_PORT || "0"),
  name: analyticsDbUrl?.[5] || process.env.ANALYTICS_DB_NAME,
  user: analyticsDbUrl?.[1] || process.env.ANALYTICS_DB_USER,
  password: analyticsDbUrl?.[2] || process.env.ANALYTICS_DB_PASSWORD
};

/** Buttondown, the newsletter: the subscribe form and the subscriber count. */
export const buttondownApiKey = process.env.BUTTONDOWN_API_KEY;

/** The SMTP server that sends inner circle sign-in links (`src/lib/mailer.tsx`). Port 465 means implicit TLS. */
export const smtp = {
  host: process.env.SMTP_HOST,
  port: Number(process.env.SMTP_PORT) || 587,
  user: process.env.SMTP_USER,
  password: process.env.SMTP_PASSWORD,
  from: process.env.SMTP_FROM
};

/**
 * The analytics database is optional. Without it the site still renders, view
 * counts just come back as null. Checking up front means an unconfigured
 * environment never dials a Postgres that was never meant to be there.
 */
export const isAnalyticsDbConfigured = Boolean(analyticsDb.host && analyticsDb.port);
